import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FORM_FIELD_FIXED_LENGTH } from 'constants/form-field.constants';
import { FormFieldType } from 'enums';
import { TranslationNamespace } from 'i18n';
import { FormField, PaymentFormValues } from 'types';

type Props = {
  fields?: FormField[];
  paymentFormValues?: PaymentFormValues;
  onPaymentFormValuesChange: (data: PaymentFormValues) => void;
};

type InputFieldProps = {
  field: FormField;
  label: string;
  placeholder: string;
  value: string;
  autoFocus?: boolean;
  required?: boolean;
  onChange: (value: string) => void;
};

const INPUT_TYPE: Partial<Record<FormFieldType, React.HTMLInputTypeAttribute>> =
  {
    [FormFieldType.FirstCardDigits]: 'tel',
    [FormFieldType.LastCardDigits]: 'tel',
    [FormFieldType.LastPhoneDigits]: 'tel',
  };

const NUMERIC_TYPES = new Set<FormFieldType>([
  FormFieldType.FirstCardDigits,
  FormFieldType.LastCardDigits,
  FormFieldType.LastPhoneDigits,
]);

const FIXED_LENGTH = FORM_FIELD_FIXED_LENGTH;

const SqInputField: React.FC<InputFieldProps> = ({
  field,
  label,
  placeholder,
  value,
  autoFocus,
  required,
  onChange,
}) => {
  const { t: tErrors } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'errors',
  });
  const [touched, setTouched] = useState(false);

  const isNumeric = useMemo(() => NUMERIC_TYPES.has(field.type), [field.type]);
  const fixedLength = useMemo(() => FIXED_LENGTH[field.type], [field.type]);
  const maxLength = useMemo(
    () => field.maxLength ?? fixedLength,
    [field.maxLength, fixedLength],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let next = e.target.value;
      if (isNumeric) {
        next = next.replace(/\D/g, '');
      }
      if (maxLength && next.length > maxLength) {
        next = next.slice(0, maxLength);
      }
      onChange(next);
    },
    [isNumeric, maxLength, onChange],
  );

  const errorMessage = useMemo(() => {
    if (!touched) {
      return null;
    }
    if (required && !value) {
      return tErrors('required');
    }
    if (!value) {
      return null;
    }
    if (fixedLength && value.length !== fixedLength) {
      return tErrors('invalid');
    }
    if (field.pattern && !new RegExp(field.pattern).test(value)) {
      return tErrors('invalid');
    }
    return null;
  }, [touched, required, value, fixedLength, field.pattern, tErrors]);

  return (
    <div>
      <div className="tw-text-sm tw-text-secondary tw-mb-1 tw-pl-4">
        {label}
        {required && (
          <span className="tw-text-[color:var(--bg-color-error-button)] tw-ml-0.5">
            {'*'}
          </span>
        )}
      </div>
      <div className="tw-bg-payment-layout-out tw-rounded-[24px] tw-px-4 tw-py-4">
        <input
          name={field.name}
          type={INPUT_TYPE[field.type] ?? 'text'}
          inputMode={isNumeric ? 'numeric' : undefined}
          value={value}
          placeholder={placeholder}
          autoFocus={autoFocus}
          maxLength={maxLength}
          className="tw-w-full tw-bg-transparent tw-text-base tw-font-medium tw-text-primary tw-outline-none tw-border-none tw-placeholder-secondary placeholder:tw-text-sm"
          onChange={handleChange}
          onBlur={() => setTouched(true)}
        />
      </div>
      {errorMessage && (
        <div className="tw-text-sm tw-text-[color:var(--bg-color-error-button)] tw-mt-1 tw-px-1">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export const SqFormFields: React.FC<Props> = ({
  fields = [],
  paymentFormValues = {},
  onPaymentFormValuesChange,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.form_builder',
  });

  const getLabel = (type: FormFieldType): string => {
    switch (type) {
      case FormFieldType.AccountNumber:
        return t('fields.account_number.label');
      case FormFieldType.Bank:
        return t('fields.bank.label');
      case FormFieldType.LastPhoneDigits:
        return t('fields.last_phone_digits.label');
      case FormFieldType.FullName:
        return t('fields.full_name.label');
      case FormFieldType.UTR:
        return t('fields.utr.label');
      case FormFieldType.IBAN:
        return t('fields.iban.label');
      case FormFieldType.FirstCardDigits:
        return t('fields.first_card_digits.label');
      case FormFieldType.LastCardDigits:
        return t('fields.last_card_digits.label');
      default:
        return '';
    }
  };

  const getPlaceholder = (type: FormFieldType): string => {
    switch (type) {
      case FormFieldType.AccountNumber:
        return t('fields.account_number.placeholder');
      case FormFieldType.Bank:
        return t('fields.bank.placeholder');
      case FormFieldType.LastPhoneDigits:
        return t('fields.last_phone_digits.placeholder');
      case FormFieldType.FullName:
        return t('fields.full_name.placeholder');
      case FormFieldType.UTR:
        return t('fields.utr.placeholder');
      case FormFieldType.IBAN:
        return t('fields.iban.placeholder');
      case FormFieldType.FirstCardDigits:
        return t('fields.first_card_digits.placeholder');
      case FormFieldType.LastCardDigits:
        return t('fields.last_card_digits.placeholder');
      default:
        return '';
    }
  };

  const visibleFields = useMemo(
    () => fields.filter((f) => !f.hidden),
    [fields],
  );

  if (!visibleFields.length) {
    return null;
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {visibleFields.map((field, index) => (
        <SqInputField
          key={field.name}
          field={field}
          label={getLabel(field.type)}
          placeholder={getPlaceholder(field.type)}
          value={paymentFormValues[field.name as keyof PaymentFormValues] ?? ''}
          autoFocus={index === 0}
          required={field.required}
          onChange={(value) =>
            onPaymentFormValuesChange({ [field.name]: value })
          }
        />
      ))}
    </div>
  );
};
